import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  DateHistogramLogRequest,
  LogRequest,
  LogsService,
  Rps,
  SiteBandwidth,
  SiteTrafficBreakdown,
  UrlBandwidth,
  UrlResponseCodesCount,
  UrlStats,
} from '@shared/services/logs/logs.service';
import { SitesService } from '@shared/services/sites/sites.service';
import { DATE_LAST_90_DAYS, DATE_NOW } from '@shared/services/calendar-data.service';
import { Bar, baseCssXAxis } from '@shared/components/highcharts/bar-horizontal/bar';
import {
  BANDWIDTH,
  BreakdownsByGraphType,
  BreakdownType,
  BreakdownTypes,
  computeInterval,
  Graph,
  GraphType, GraphTypes,
  RESPONSE_TIME,
  RESPONSES_CODES,
  TOP_IP_BY_TRAFFIC,
  TOP_SITES_BY_BANDWIDTH,
  TOP_SITES_BY_TRAFFIC,
  TOP_URLS_BY_BANDWIDTH,
  TOP_URLS_BY_ERROR_CODES,
  TOP_URLS_BY_RESPONSE_TIME,
  TOP_URLS_RESPONSE_TIME_SUM,
  TOP_USER_AGENTS_BY_TRAFFIC,
  TRAFIC,
} from '@shared/components/highcharts/graph/graph';
import { ClickableType, ClickEvent, GraphComponent } from '@shared/components/highcharts/graph/graph.component';
import { DateRange } from '@shared/utils/date-range';
import { TrackEvent } from 'app/theme/dashboard/track.event';
import Highcharts from 'highcharts';
import _ from 'lodash';
import moment from 'moment';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '@shared/services/auth.service';
import { Filter, FiltersEnum, formatFiltersLogs } from '../filters/filters';
import { ContextEnum } from '@shared/models/ContextSiteModel';
import { UAChoices } from '../filters/filters.component';
import { isValidIP } from '@shared/validators/validators';
import { countryCodeToEmoji, countryEmojiToCountryCode } from '@shared/utils/flags';
import { DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { BarHorizontalComponent } from '@shared/components/highcharts/bar-horizontal/bar-horizontal.component';
import { DurationSecondsPipe, SizePipe, TranslateLabelPipe } from '@shared/pipe/filter.pipe';

@Component({
  selector: 'app-my-logs-graph',
  templateUrl: './my-logs-graphs.component.html',
  styleUrls: ['./my-logs-graphs.component.scss'],
  imports: [
    NgClass,
    GraphComponent,
    NgIf,
    SpinnerComponent,
    NgFor,
    BarHorizontalComponent,
    DecimalPipe,
    DurationSecondsPipe,
    SizePipe,
    TranslatePipe,
    TranslateLabelPipe,
  ],
})
export class MyLogsGraphsComponent implements OnInit {
  @Input() filters: Filter[];
  @Input() ctx: ContextEnum;
  @Input() isLoading: boolean = false;
  @Input() totalRequests: number = null;
  @Input() totalBandwidth: number = null;
  @Input() breakdownPanelIsOpen: boolean = true;

  @Output() track = new EventEmitter<TrackEvent>();
  @Output() getLogs = new EventEmitter<void>();
  @Output() onPeriodEmitted = new EventEmitter<DateRange>();

  @ViewChild('graphOverTimePanel') graphOverTimePanel: ElementRef;
  @ViewChild('breakdownsPanel') breakdownsPanel: ElementRef;
  @ViewChild('breakdownGraphsContainer') breakdownGraphsContainer: ElementRef;
  @ViewChild('breakdownGraphs') breakdownGraphs: ElementRef;

  breakpointSubscription: Subscription;

  graphType: GraphType;
  breakdownType: BreakdownType;

  lang: string;
  Math = Math;

  TRAFIC: GraphType = TRAFIC;
  RESPONSE_TIME: GraphType = RESPONSE_TIME;
  RESPONSES_CODES: GraphType = RESPONSES_CODES;
  BANDWIDTH: GraphType = BANDWIDTH;

  protected readonly BreakdownsByGraphType: Record<GraphType, BreakdownType[]>;

  graphOverTimeData: Partial<{ [property in keyof GraphType]: Highcharts.Chart }> = {};
  breakdownsData: Partial<{ [property in keyof BreakdownType]: Highcharts.Chart }> = {};

  clickableTypes: { [prop in GraphType]: ClickableType } = {
    traffic: 'column',
    bandwidth: 'spline',
    responseTime: 'spline',
    responsesCodes: 'column',
  };

  chartInterval = null;

  graphsRendered = {
    overTime: false,
    breakdown: false,
  };

  constructor(
    private auth: AuthService,
    private logsService: LogsService,
    public sites: SitesService,
    public translate: TranslateService,
    private router: Router,
    private graph: Graph,
    private bar: Bar,
    private renderer: Renderer2,
  ) {
    this.BreakdownsByGraphType = BreakdownsByGraphType;
    this.graphType = this.getRouterParam('graphType') || TRAFIC;
    this.breakdownType =
      (this.getRouterParam('graphSubType') as BreakdownType) ?? BreakdownsByGraphType[this.graphType][0];
  }

  ngOnInit() {
    this.lang = this.auth.getCurrentLanguage();
  }

  ngAfterViewInit() {
    if (!this.breakdownPanelIsOpen) this.closeBreakdownsPanel();
  }

  ngOnDestroy() {
    if (this.breakpointSubscription) {
      this.breakpointSubscription.unsubscribe();
    }
  }

  resetData() {
    this.graphOverTimeData = {};
    this.breakdownsData = {};
  }

  onPeriodEmittedFromZoneSelected(p: DateRange) {
    this.onPeriodEmitted.emit(p);
  }

  toggleBreakdownsPanel() {
    this.breakdownsPanel.nativeElement.classList.contains('collapsed')
      ? this.openBreakdownsPanel()
      : this.closeBreakdownsPanel();
  }

  openBreakdownsPanel() {
    this.breakdownPanelIsOpen = true;
    if (!this.breakdownsData[this.breakdownType]?.series?.length) {
      this.loadBreakdownData();
    }
    this.breakdownsPanel.nativeElement.classList.remove('collapsed');
    this.graphOverTimePanel.nativeElement.classList.remove('expanded');
    this.renderer.setStyle(this.breakdownGraphs.nativeElement, 'min-width', 'unset');
  }

  closeBreakdownsPanel() {
    this.breakdownPanelIsOpen = false;
    const minWidth = this.breakdownGraphs.nativeElement.offsetWidth;
    this.renderer.setStyle(this.breakdownGraphs.nativeElement, 'min-width', `${minWidth}px`); // chart doesn't collapse during opening with this hack
    this.graphOverTimePanel.nativeElement.classList.add('expanded');
    this.breakdownsPanel.nativeElement.classList.add('collapsed');
  }

  async setGraphType(graphType: GraphType) {
    this.graphType = graphType;
    this.breakdownType = BreakdownsByGraphType[graphType][0];
    return this.loadGraphs();
  }

  async loadGraphs() {
    return Promise.all([this.loadGraphOverTime(), this.loadBreakdownData()]);
  }

  async loadGraphOverTime() {
    const type = this.graphType;

    if (this.graphOverTimeData[type]?.series?.length) {
      // already have data for this selection, no need to reload
      return;
    }

    this.graphsRendered.overTime = false;
    try {
      let res = await firstValueFrom(
        this.logsService.getDateHistograms(type, this.getDateHistogramLogRequest(), this.ctx),
      );
      this.graphOverTimeData = {
        ...this.graphOverTimeData,
        [type]: _.merge(this.graph.formatGraphData(type, res), { chart: { spacingBottom: 0 }})
      };
      if (type == 'bandwidth') {
        this.totalBandwidth = _.sum(res.bandwidth.data.map((data) => data[1]));
      }
    } catch (error) {
      console.error('Error during rendering graph', error);
    } finally {
      this.graphsRendered.overTime = true;
    }
  }

  async setBreakdownType(breakdownType: BreakdownType) {
    this.breakdownType = breakdownType;
    return this.loadBreakdownData();
  }

  async loadBreakdownData() {
    if (!this.breakdownPanelIsOpen) {
      return;
    }

    const type = this.breakdownType;
    if (this.breakdownsData[type]?.series?.length) {
      // already have data for this selection, no need to reload
      return;
    }

    this.graphsRendered.breakdown = false;
    try {
      this.breakdownsData[type] = await new graphBuilders[type](this.logsService, this.bar).buildGraph(
        this.getLogRequest(),
        this.ctx,
      );
    } catch (error) {
      console.error('Error during rendering graph', error);
    } finally {
      this.graphsRendered.breakdown = true;
    }
  }

  breakdownCategoryClicked(category: string) {
    const [site, path] = this.parseSiteAndPath(category);
    switch (this.breakdownType) {
      case 'topSitesByTraffic':
        this.sites.setSites(this.ctx, [category]);
        this.track.emit({});

        break;

      case 'topUrlsByBandwidth':
      case 'topSitesByBandwidth':
      case 'topUrlsByErrorCodes':
      case 'topUrlsByResponseTime':
      case 'topUrlsByResponseTimeSum':
        this.sites.setSites(this.ctx, [site]);
        this.track.emit({ [FiltersEnum.PATH]: path });
        break;
      case 'topTrafficByIP':
        if (isValidIP(category)) {
          this.track.emit({ [FiltersEnum.IP]: category });
        } else {
          this.track.emit({ [FiltersEnum.COUNTRY]: countryEmojiToCountryCode(category) });
        }
        break;
      case 'topUserAgentsByTraffic':
        this.track.emit({ [FiltersEnum.USER_AGENT]: this.uaValueFromSlug(category) });
    }
  }
  parseSiteAndPath(siteAndPath: string): string[] {
    const [site, ...rest] = siteAndPath.split('/');
    return [site, '/' + rest.join('/')];
  }
  breakdownBarClicked(event) {
    const value = event?.point?.series?.userOptions?.custom?.slug;
    const category = event?.point?.category;

    switch (this.breakdownType) {
      case 'topSitesByTraffic':
        this.sites.setSites(this.ctx, [category]);
        this.track.emit({ [FiltersEnum.ACTION]: value });
        break;

      case 'topUrlsByErrorCodes': {
        const [site, path] = this.parseSiteAndPath(category);

        this.sites.setSites(this.ctx, [site]);
        this.track.emit({ [FiltersEnum.PATH]: path, [FiltersEnum.STATUS_CODE]: value });
        break;
      }

      case 'topSitesByBandwidth':
      case 'topUrlsByBandwidth': {
        const [site, path] = this.parseSiteAndPath(category);

        this.sites.setSites(this.ctx, [site]);
        this.track.emit({
          [FiltersEnum.PATH]: path,
          [FiltersEnum.CACHE_STATUS]: value,
        });
        break;
      }

      case 'topUrlsByResponseTime':
      case 'topUrlsByResponseTimeSum': {
        const [site, path] = this.parseSiteAndPath(category);

        this.sites.setSites(this.ctx, [site]);
        this.track.emit({ [FiltersEnum.PATH]: path });
        break;
      }
      case 'topTrafficByIP':
        this.track.emit({ [FiltersEnum.IP]: category, [FiltersEnum.ACTION]: value });
        break;
      case 'topUserAgentsByTraffic':
        this.track.emit({
          [FiltersEnum.USER_AGENT]: this.uaValueFromSlug(category),
          [FiltersEnum.ACTION]: value,
        });
        break;
    }
  }
  private uaValueFromSlug(slug: string): string {
    return UAChoices.find((c) => c.slug === slug)?.value ?? slug;
  }
  zoomOut() {
    const range = this.sites[this.ctx].current.period.range;
    const interval: number = range.durationMs();

    let start: moment.Moment = moment.max([
      moment(range.start).subtract(Math.round(interval / 2)),
      DATE_LAST_90_DAYS(),
    ]);
    const end: moment.Moment = moment.min([moment(start).add(interval * 2), DATE_NOW()]);
    start = moment.max([moment(end).subtract(interval * 2), DATE_LAST_90_DAYS()]);

    this.onPeriodEmitted.emit(new DateRange(start, end));
  }

  strafe(direction: StrafeDirection) {
    const now = DATE_NOW();
    const period = this.sites[this.ctx].current.period.range;
    const interval = period.durationMs();
    const shift = Math.round(interval / 2);

    let start,
      end: moment.Moment = null;
    if (direction == 'right') {
      end = moment.min(now, moment(period.end).add(shift));
      start = moment(end).subtract(interval);
    } else if (direction == 'left') {
      start = moment.max(DATE_LAST_90_DAYS(), moment(period.start).subtract(shift));
      end = moment(start).add(interval);
    }

    this.onPeriodEmitted.emit(new DateRange(start, end));
  }

  canGoForward(end) {
    return moment(end).isBefore(moment());
  }

  disableGoForward(end) {
    return moment(end).isSame(moment(), 'minute') || !this.canGoBack(end);
  }

  canGoBack(start) {
    return moment(start).isAfter(DATE_LAST_90_DAYS());
  }

  disableGoBack(start) {
    return moment(start).isSame(DATE_LAST_90_DAYS(), 'minute') || !this.canGoForward(start);
  }

  onColumnClick(event: ClickEvent) {
    switch (event.category) {
      case RESPONSE_TIME:
        return this.track.emit({ [FiltersEnum.RESPONSE_TIME_GTE]: event.slug });
      case TRAFIC:
        return this.track.emit({ [FiltersEnum.ACTION]: event.slug });
      case RESPONSES_CODES:
        return this.track.emit({ [FiltersEnum.STATUS_CODE]: event.slug });
      case BANDWIDTH:
        return this.track.emit({ [FiltersEnum.CONTENT_LENGTH_GTE]: event.slug });
    }
  }

  getDateHistogramLogRequest(): DateHistogramLogRequest {
    this.chartInterval = computeInterval(this.sites[this.ctx].current.period.range);
    return {
      ...this.getLogRequest(),
      aggregationInterval: this.formatInterval(this.chartInterval),
    };
  }
  formatInterval(seconds: number): string {
    if (seconds < 1) {
      return `${Math.max(1, Math.round(seconds * 1000))}ms`;
    }
    return `${Math.max(1, Math.round(seconds))}s`;
  }
  getLogRequest(): LogRequest {
    return { ...this.sites[this.ctx].buildRequestData(), ...formatFiltersLogs(this.filters) };
  }

  getRouterParam(param) {
    return this.router?.getCurrentNavigation()?.extras?.state?.[param];
  }

  noSitesFallback() {
    // if no sites, give to highcharts fake chart with empty data
    Object.keys(this.graphsRendered).forEach((key) => (this.graphsRendered[key] = true));
    BreakdownTypes.forEach((t) => {
      this.breakdownsData[t] = {
        chart: {},
        lang: {},
        title: { text: null },
        credits: { enabled: false },
        plotOptions: { series: { cursor: null, events: {} } },
      };
    });
    GraphTypes.forEach((t) => {
      this.graphOverTimeData[t] = { chart: {}, lang: {}, title: { text: null }, credits: { enabled: false } };
    });
  }
}

type StrafeDirection = 'left' | 'right';

abstract class BreakdownGraphBuilder<T> {
  constructor(
    protected readonly logs: LogsService,
    protected readonly bars: Bar,
  ) {}

  protected abstract getData(request: LogRequest, context: ContextEnum): Observable<T[]>;

  protected abstract formatData(data: T[]): Highcharts.Options;

  public async buildGraph(request: LogRequest, context: ContextEnum): Promise<Highcharts.Options> {
    const data = await firstValueFrom(this.getData(request, context));
    return this.formatData(data);
  }
}

class TopSitesByTrafficBreakdown extends BreakdownGraphBuilder<SiteTrafficBreakdown & Rps> {
  protected formatData(data: (SiteTrafficBreakdown & Rps)[]): Highcharts.Options {
    return this.bars.formatDataTrafic(data, 'TotalPerSite');
  }

  protected getData(request: LogRequest, context: ContextEnum): Observable<(SiteTrafficBreakdown & Rps)[]> {
    return this.logs.getTrafficBreakdown('traffic/top-sites/by-total', request, context);
  }
}

class TopTrafficByIP extends BreakdownGraphBuilder<SiteTrafficBreakdown & Rps> {
  protected formatData(data: (SiteTrafficBreakdown & Rps)[]): Highcharts.Options {
    const flagByIp = new Map<string, string>();
    for (const d of data) {
      const countryCode = (d as any).countryCode ?? (d as any).country;

      if (countryCode) {
        flagByIp.set(d.site, countryCodeToEmoji(countryCode));
      }
    }
    let barCharts = this.bars.formatDataTrafic(data, 'TopTrafficByIP');
    barCharts.xAxis.labels = {
      ...barCharts.xAxis.labels,
      formatter: function () {
        const flag = flagByIp?.get(this.value as string);
        const label = flag
          ? `<span dy="2px" class="category-override">${flag}</span><span dy="-2px"> ${this.value}</span>`
          : `${this.value}`;
        return `<div title="${this.value}" style="${baseCssXAxis}">${label}</div>`;
      },
    };
    barCharts.xAxis.labels.align = 'left';
    barCharts.xAxis.labels.reserveSpace = true;
    return barCharts;
  }

  protected getData(request: LogRequest, context: ContextEnum): Observable<(SiteTrafficBreakdown & Rps)[]> {
    return this.logs
      .getTrafficBreakdown('traffic/top-ips/by-total', request, context)
      .pipe(map((items) => items.map((item) => ({ ...item, site: (item as any).key }))));
  }
}

class TopUserAgentsByTrafficBreakdown extends BreakdownGraphBuilder<SiteTrafficBreakdown & Rps> {
  private static readonly uaSlugByValue: Map<string, string> = new Map(UAChoices.map((c) => [c.value, c.slug]));

  protected formatData(data: (SiteTrafficBreakdown & Rps)[]): Highcharts.Options {
    const barCharts = this.bars.formatDataTrafic(data, 'TotalPerUserAgent');
    const slugByValue = TopUserAgentsByTrafficBreakdown.uaSlugByValue;
    barCharts.xAxis.labels = {
      ...barCharts.xAxis.labels,
      formatter: function () {
        return String(slugByValue.get(this.value as string) ?? this.value);
      },
    };
    return barCharts;
  }

  protected getData(request: LogRequest, context: ContextEnum): Observable<(SiteTrafficBreakdown & Rps)[]> {
    return this.logs
      .getTrafficBreakdown('traffic/top-user-agents/by-total', request, context)
      .pipe(map((items) => items.map((item) => ({ ...item, site: (item as any).key }))));
  }
}

class ResponseCodeBreakdown extends BreakdownGraphBuilder<UrlResponseCodesCount> {
  protected formatData(data: UrlResponseCodesCount[]): Highcharts.Options {
    return this.bars.formatDataResponsesCodes(data);
  }

  protected getData(request: LogRequest, context: ContextEnum): Observable<UrlResponseCodesCount[]> {
    return this.logs
      .getUrlBreakdown<UrlResponseCodesCount>('top-urls-by-http-error', request, context)
      .pipe(map((items) => _.orderBy(items, [(item) => item.count4xx + item.count5xx], ['desc'])));
  }
}

class ResponseTimeBreakdown extends BreakdownGraphBuilder<UrlStats> {
  protected formatData(data: UrlStats[]): Highcharts.Options {
    return this.bars.formatDataResponseTime(data);
  }

  protected getData(request: LogRequest, context: ContextEnum): Observable<UrlStats[]> {
    return this.logs.getUrlBreakdown('slow-urls', request, context);
  }
}

class ResponseTimeSumBreakdown extends BreakdownGraphBuilder<UrlStats> {
  protected formatData(data: UrlStats[]): Highcharts.Options {
    return this.bars.formatDataResponseTimeSum(data);
  }

  protected getData(request: LogRequest, context: ContextEnum): Observable<UrlStats[]> {
    return this.logs.getUrlBreakdown('cpu-consuming-urls', request, context);
  }
}

class TopUrlByBandwidthBreakdown extends BreakdownGraphBuilder<UrlBandwidth> {
  protected formatData(data: UrlBandwidth[]): Highcharts.Options {
    return this.bars.formatDataBandwidth(data);
  }

  protected getData(request: LogRequest, context: ContextEnum): Observable<UrlBandwidth[]> {
    return this.logs.getUrlBreakdown('top-urls-by-bandwidth', request, context);
  }
}

class TopSitesByBandwidthBreakdown extends BreakdownGraphBuilder<SiteBandwidth> {
  protected formatData(data: SiteBandwidth[]): Highcharts.Options {
    return this.bars.formatTopSitesByBandwidthBreakdown(data);
  }

  protected getData(request: LogRequest, context: ContextEnum): Observable<SiteBandwidth[]> {
    return this.logs.getBreakdown(context, 'top-sites-by-bandwidth', request);
  }
}

type BreakdownGraphBuilderConstructor<T extends BreakdownGraphBuilder<any>> = new (logs: LogsService, bars: Bar) => T;
const graphBuilders: Record<BreakdownType, BreakdownGraphBuilderConstructor<any>> = {
  [TOP_SITES_BY_TRAFFIC]: TopSitesByTrafficBreakdown,
  [TOP_URLS_BY_RESPONSE_TIME]: ResponseTimeBreakdown,
  [TOP_URLS_RESPONSE_TIME_SUM]: ResponseTimeSumBreakdown,
  [TOP_URLS_BY_ERROR_CODES]: ResponseCodeBreakdown,
  [TOP_URLS_BY_BANDWIDTH]: TopUrlByBandwidthBreakdown,
  [TOP_SITES_BY_BANDWIDTH]: TopSitesByBandwidthBreakdown,
  [TOP_USER_AGENTS_BY_TRAFFIC]: TopUserAgentsByTrafficBreakdown,
  [TOP_IP_BY_TRAFFIC]: TopTrafficByIP,
};
